// § 35a (#564 S5c): KI-Vorschläge holen, prüfen, übernehmen. Nur Büro. Die Rechnung ändert sich erst bei „Übernehmen“.
import { supabase } from '../supabase/client';

export type Art35a = 'handwerker' | 'haushaltsnah' | 'keine' | 'unklar';
export interface Vorschlag35a {
  rechnung_id: string; art: Art35a; lohn: number | null; material: number | null; fahrt: number | null;
  fundstelle: string | null; begruendung: string | null; status: 'vorschlag' | 'bestaetigt' | 'verworfen'; erstellt_am: string;
}
export interface Rechnung35a {
  id: string; rechnungsnummer: string | null; unternehmen: string | null; bezeichnung: string | null; brutto: number | null;
  datum: string | null; bezahlt_timestamp: string | null; dokument_id: string | null; lohnanteil_35a: number | null;
}

export const ART_TEXT: Record<Art35a, string> = { handwerker: 'Handwerkerleistung', haushaltsnah: 'Haushaltsnahe Dienstleistung', keine: 'Nicht begünstigt', unklar: 'Unklar' };
const n = (v: unknown) => (v == null ? null : Number(v));

export async function ladeSchalter35a(): Promise<boolean> {
  const { data } = await supabase.from('app_settings').select('value').eq('key', 'ki_35a').maybeSingle();
  return (data?.value as { aktiv?: boolean } | null)?.aktiv === true;
}
export async function setzeSchalter35a(aktiv: boolean): Promise<void> {
  const { error } = await supabase.from('app_settings').upsert({ key: 'ki_35a', value: { aktiv }, updated_at: new Date().toISOString() }, { onConflict: 'key' });
  if (error) throw error;
}

/** Eingangsrechnungen eines Objekts mit Datum oder Zahlung im Jahr. */
export async function ladeRechnungen35a(objektId: string, jahr: number): Promise<Rechnung35a[]> {
  const von = `${jahr}-01-01`; const bis = `${jahr}-12-31`;
  const { data, error } = await supabase.from('rechnungen')
    .select('id, rechnungsnummer, unternehmen, bezeichnung, brutto, datum, bezahlt_timestamp, dokument_id, lohnanteil_35a')
    .eq('objekt_id', objektId).or('richtung.is.null,richtung.eq.eingang').is('archiviert_am', null)
    .or(`and(datum.gte.${von},datum.lte.${bis}),and(bezahlt_timestamp.gte.${von},bezahlt_timestamp.lte.${bis}T23:59:59)`)
    .order('datum', { ascending: true });
  if (error) throw error;
  return ((data ?? []) as Rechnung35a[]).map((r) => ({ ...r, brutto: n(r.brutto), lohnanteil_35a: n(r.lohnanteil_35a) }));
}

export async function ladeRechnung35a(id: string): Promise<Rechnung35a | null> {
  const { data, error } = await supabase.from('rechnungen')
    .select('id, rechnungsnummer, unternehmen, bezeichnung, brutto, datum, bezahlt_timestamp, dokument_id, lohnanteil_35a').eq('id', id).maybeSingle();
  if (error) throw error;
  return data ? { ...(data as Rechnung35a), brutto: n(data.brutto), lohnanteil_35a: n(data.lohnanteil_35a) } : null;
}

export async function ladeVorschlaege35a(rechnungIds: readonly string[]): Promise<Map<string, Vorschlag35a>> {
  const m = new Map<string, Vorschlag35a>();
  for (let i = 0; i < rechnungIds.length; i += 200) {
    const { data, error } = await supabase.from('rechnung_35a_vorschlaege').select('*').in('rechnung_id', rechnungIds.slice(i, i + 200));
    if (error) throw error;
    for (const v of (data ?? []) as Vorschlag35a[]) m.set(v.rechnung_id, { ...v, lohn: n(v.lohn), material: n(v.material), fahrt: n(v.fahrt) });
  }
  return m;
}

async function fehlerAus(error: unknown, data: unknown): Promise<string> {
  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === 'function') { try { const j = await ctx.json(); if (j?.error) return String(j.error); } catch { /* leer */ } }
  return (data as { error?: string } | null)?.error ?? (error as Error)?.message ?? 'Unbekannter Fehler';
}

/** Text des PDFs sicherstellen (vorhandenes Auslesen inkl. OCR), dann KI-Vorschlag holen. */
export async function holeVorschlag35a(r: Pick<Rechnung35a, 'id' | 'dokument_id'>): Promise<void> {
  if (!r.dokument_id) throw new Error('Zur Rechnung gibt es kein PDF.');
  const { data: dok } = await supabase.from('dokumente').select('extracted_text').eq('id', r.dokument_id).maybeSingle();
  if (!((dok?.extracted_text as string | null) ?? '').trim()) {
    const t = await supabase.functions.invoke('dokument-text-extract', { body: { dokument_id: r.dokument_id } });
    if (t.error) throw new Error(`Text nicht ausgelesen: ${await fehlerAus(t.error, t.data)}`);
  }
  const { data, error } = await supabase.functions.invoke('rechnung-35a', { body: { rechnung_id: r.id } });
  if (error) throw new Error(await fehlerAus(error, data));
}

/** Büro übernimmt (ggf. korrigiert): Lohn inkl. Fahrt → rechnungen.lohnanteil_35a; Art (Abs. 2/3) im Vorschlag. */
export async function uebernehme35a(rechnungId: string, betrag: number, art: 'handwerker' | 'haushaltsnah'): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase.from('rechnungen').update({ lohnanteil_35a: betrag }).eq('id', rechnungId);
  if (error) throw error;
  const jetzt = new Date().toISOString();
  const { data: alt } = await supabase.from('rechnung_35a_vorschlaege').select('rechnung_id').eq('rechnung_id', rechnungId).maybeSingle();
  const v = alt
    ? await supabase.from('rechnung_35a_vorschlaege').update({ art, status: 'bestaetigt', geprueft_von: user?.id ?? null, geprueft_am: jetzt }).eq('rechnung_id', rechnungId)
    : await supabase.from('rechnung_35a_vorschlaege').insert({ rechnung_id: rechnungId, art, lohn: betrag, begruendung: 'Von Hand eingetragen', status: 'bestaetigt', geprueft_von: user?.id ?? null, geprueft_am: jetzt });
  if (v.error) throw v.error;
}

/** Nicht begünstigt bzw. Vorschlag falsch: Lohnanteil 0 in der Rechnung, Vorschlag „verworfen“. */
export async function verwerfe35a(rechnungId: string): Promise<void> {
  const { data: { user } } = await supabase.auth.getUser();
  const { error } = await supabase.from('rechnungen').update({ lohnanteil_35a: 0 }).eq('id', rechnungId);
  if (error) throw error;
  await supabase.from('rechnung_35a_vorschlaege').upsert({ rechnung_id: rechnungId, art: 'keine', status: 'verworfen', geprueft_von: user?.id ?? null, geprueft_am: new Date().toISOString() }, { onConflict: 'rechnung_id' });
}

/** Entscheidung zurücknehmen: Lohnanteil leeren, Vorschlag löschen. */
export async function setze35aZurueck(rechnungId: string): Promise<void> {
  const { error } = await supabase.from('rechnungen').update({ lohnanteil_35a: null }).eq('id', rechnungId);
  if (error) throw error;
  await supabase.from('rechnung_35a_vorschlaege').delete().eq('rechnung_id', rechnungId);
}
